import {
  Component,
  ElementRef,
  AfterViewInit,
  OnChanges,
  Input,
} from '@angular/core';
import { ChartComponent } from '../chart.component';
import { select, event } from 'd3-selection';
import { axisBottom } from 'd3-axis';
import { min, max } from 'd3-array';
import { scaleTime } from 'd3-scale';
import { zoom } from 'd3-zoom';
import { TimelineData, TimelineTypes } from './timeline-chart-base.model';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-timeline-chart-base',
  template: '',
})
export class TimelineChartBaseComponent
  extends ChartComponent
  implements AfterViewInit, OnChanges {
  @Input() data: TimelineData;
  @Input() timelineHeight = 50;
  @Input() avtiveRunColor: string; // '#d5eae8';
  @Input() inavtiveRunColor: string; // '#527c9f';
  @Input() strongHitIcon: string; // './assets/reports/forklift-hit-strong.svg';
  @Input() weakHitIcon: string; // './assets/reports/forklift-hit-weak.svg';
  private xScale: any;
  private xAxis;
  private timepointSvg;
  private timelineSvg;
  private chartSvg;
  private minValue: Date;
  private maxValue: Date;
  constructor(
    protected el: ElementRef,
    private translateService: TranslateService
  ) {
    super(el);
  }
  private zoom = () => {
    this.updateChart();
  };
  ngAfterViewInit() {
    this.drawChart();
  }

  ngOnChanges(): void {
    if (!this.data) {
      return;
    }
    this.drawChart();
  }

  drawChart() {
    this.updateChartDimentions();
    if (this.width && this.height && this.data) {
      this.updateDimensions();
      this.initElements();
      this.setScales();
      this.drawBackground();
      this.drawTimeline();
      this.drawTimepoints();
      this.addAxis();
    }
  }

  private updateDimensions() {
    this.height = this.height - this.margin.top - this.margin.bottom;
    this.width = this.width - this.margin.left - this.margin.right;
  }

  private initElements() {
    this.svg = select(this.chartElement)
      .append('svg')
      .attr('width', this.width + 90)
      .attr('height', this.height + this.margin.top + this.margin.bottom)
      .append('g')
      .attr('transform', `translate(45,${this.margin.top})`);

    this.chartSvg = this.svg
      .append('svg')
      .attr('width', this.width + 1)
      .attr('height', this.height + this.margin.top + this.margin.bottom)
      .attr('style', 'cursor:pointer;')
      .append('g')
      .call(
        zoom()
          .scaleExtent([1, this.data.timeline.length / 2])
          .translateExtent([
            [0, 0],
            [this.width, this.height],
          ])
          .extent([
            [0, 0],
            [this.width, this.height],
          ])
          .on('zoom', this.zoom)
      );
  }

  private setScales() {
    this.minValue = min(this.data.timeline.map((el) => el.timeFrom));
    this.maxValue = max(this.data.timeline.map((el) => el.timeTo));
    this.xScale = scaleTime()
      .domain([this.minValue, this.maxValue])
      .range([0, this.width]);
  }

  private drawBackground() {
    this.chartSvg
      .selectAll('g')
      .data([null])
      .enter()
      .append('rect')
      .attr('width', this.xScale(this.maxValue))
      .attr('x', this.xScale(this.minValue))
      .attr('height', this.timelineHeight)
      .attr('fill', '#e5e5e5');
  }

  private drawTimeline() {
    this.timelineSvg = this.chartSvg
      .selectAll('g')
      .data(this.data.timeline)
      .enter()
      .append('rect')
      .attr('width', (d) => this.xScale(d.timeTo) - this.xScale(d.timeFrom))
      .attr('x', (d) => this.xScale(d.timeFrom))
      .attr('height', this.timelineHeight)
      .attr('fill', (d) =>
        d.type === TimelineTypes.ACTIVE
          ? this.avtiveRunColor
          : this.inavtiveRunColor
      )
      .on('click', (d, i, elements) =>
        this.clickElement.emit({ d, i, elements })
      )
      .on('mouseover', (d, i, elements) =>
        this.mouseOverElement.emit({ d, i, elements })
      )
      .on('mouseout', (d, i, elements) =>
        this.mouseOutElement.emit({ d, i, elements })
      );
  }

  private drawTimepoints() {
    const iconSize = this.timelineHeight / 2.5;
    this.timepointSvg = this.chartSvg
      .selectAll('g')
      .data(this.data.timepoints)
      .enter()
      .append('svg:image')
      .attr('xlink:href', (d) =>
        d.type === TimelineTypes.STRONG ? this.strongHitIcon : this.weakHitIcon
      )
      .attr('width', iconSize)
      .attr('height', iconSize)
      .attr('x', (d) => this.xScale(d.time))
      .attr('y', (this.timelineHeight - iconSize) / 2)
      .on('click', (d, i, elements) =>
        this.clickElement.emit({ d, i, elements })
      )
      .on('mouseover', (d, i, elements) =>
        this.mouseOverElement.emit({ d, i, elements })
      )
      .on('mouseout', (d, i, elements) =>
        this.mouseOutElement.emit({ d, i, elements })
      );
  }

  private addAxis() {
    // Add the X Axis
    this.xAxis = this.svg
      .append('g')
      .attr('transform', `translate(0,${this.height})`)
      .attr('style', `font-size:12px; font-weight:600`)
      .call(
        axisBottom(this.xScale)
          .ticks(3)
          .tickFormat((date) => {
            return moment(date as any)
              .locale(this.translateService.currentLang)
              .format(
                moment(date as any).hours() === 0 &&
                  moment(date as any).minutes() === 0 &&
                  moment(date as any).seconds() === 0
                  ? 'L'
                  : 'LT'
              );
          })
      );
  }

  updateChart() {
    const newX = event.transform.rescaleX(this.xScale);
    const ticks = event.transform.k <= 4 ? event.transform.k : 4;
    this.timelineSvg
      .attr('width', (d) => newX(d.timeTo) - newX(d.timeFrom))
      .attr('x', (d) => newX(d.timeFrom));
    this.timepointSvg.attr('x', (d) => newX(d.time));
    this.xAxis.call(
      axisBottom(newX)
        .ticks(3 * ticks)
        .tickFormat((date) => {
          return moment(date as any)
            .locale(this.translateService.currentLang)
            .format(
              moment(date as any).hours() === 0 &&
                moment(date as any).minutes() === 0 &&
                moment(date as any).seconds() === 0
                ? 'L'
                : 'LT'
            );
        })
    );
  }
}
